import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Banknote, CreditCard, Smartphone, TrendingUp, User, Printer } from "lucide-react";

interface SpaPayment { id: number; bookingid: string; amount: number; method: string; note?: string; card_number?: string; transaction_ref?: string; created_at: string; }
interface SpaSettlement { payments: SpaPayment[]; summary: { cash: number; cash_count: number; card: number; card_count: number; instapay: number; instapay_count: number; total: number; }; therapists: { name: string; sessions: number; revenue: number; commission: number }[]; }
function authH() { return { Authorization: `Bearer ${localStorage.getItem("ai2_token")}`, "x-hotel": localStorage.getItem("ai2_hotel") || "" }; }
function fmtDT(s: string) { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
function todayStr() { return new Date().toISOString().slice(0, 10); }

export default function SpaSettlement({ API }: { API: string }) {
  const [date, setDate]         = useState(todayStr());
  const [data, setData]         = useState<SpaSettlement | null>(null);
  const [loading, setLoading]   = useState(true);

  const fetch = useCallback(async (d: string) => {
    setLoading(true);
    try { const { data: res } = await axios.get(`${API}/settlement?date=${d}`, { headers: authH() }); setData(res); }
    catch { /**/ } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetch(date); }, [date, fetch]);

  const methodLabel = (p: SpaPayment) => {
    const m = p.method?.toLowerCase();
    if (m === "visa" || m === "card") return p.card_number ? `Card ****${p.card_number}` : "Card";
    if (m === "instapay") return p.transaction_ref ? `Instapay (${p.transaction_ref})` : "Instapay";
    return "Cash";
  };

  const print = () => {
    if (!data) return;
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`<html><head><title>Spa Settlement — ${date}</title><style>body{font-family:sans-serif;padding:24px;color:#111}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{padding:8px 12px;border-bottom:1px solid #e2e8f0;text-align:left}th{background:#f1f5f9}h2{margin:0 0 4px}p{margin:0 0 16px;color:#555;font-size:12px}</style></head><body>`);
    win.document.write(`<h2>Spa Settlement — ${date}</h2><p>Generated ${new Date().toLocaleString("en-GB")}</p>`);
    win.document.write(`<h3>Summary</h3><table><tr><th>Method</th><th>Transactions</th><th>Total</th></tr><tr><td>Cash</td><td>${data.summary.cash_count}</td><td>$${data.summary.cash.toFixed(2)}</td></tr><tr><td>Card</td><td>${data.summary.card_count}</td><td>$${data.summary.card.toFixed(2)}</td></tr><tr><td>Instapay</td><td>${data.summary.instapay_count}</td><td>$${data.summary.instapay.toFixed(2)}</td></tr><tr><td><strong>Total</strong></td><td><strong>${data.payments.length}</strong></td><td><strong>$${data.summary.total.toFixed(2)}</strong></td></tr></table>`);
    if (data.therapists.length > 0) {
      win.document.write(`<h3 style="margin-top:24px">Therapist Commission (10%)</h3><table><tr><th>Therapist</th><th>Sessions</th><th>Revenue</th><th>Commission</th></tr>${data.therapists.map(t => `<tr><td>${t.name}</td><td>${t.sessions}</td><td>$${t.revenue.toFixed(2)}</td><td>$${t.commission.toFixed(2)}</td></tr>`).join("")}</table>`);
    }
    win.document.write(`<h3 style="margin-top:24px">Transactions</h3><table><tr><th>Ref</th><th>Note</th><th>Method</th><th>Amount</th><th>Time</th></tr>${data.payments.map(p => `<tr><td>${p.bookingid}</td><td>${p.note || "—"}</td><td>${methodLabel(p)}</td><td>$${p.amount?.toFixed(2)}</td><td>${fmtDT(p.created_at)}</td></tr>`).join("")}</table>`);
    win.document.write(`</body></html>`); win.document.close(); win.print();
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#F2EBD9" }}>Spa Settlement</div>
          <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginTop: 2 }}>Payments and therapist commission</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="spa-date-input" style={{ width: "auto" }} />
          <button className="spa-btn-ghost" onClick={print}><Printer size={14} /> Print</button>
        </div>
      </div>

      {loading ? <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading…</div> : !data ? null : (
        <>
          {/* Summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
            {[
              { icon: <Banknote size={18} />, label: "Cash", value: data.summary.cash, count: data.summary.cash_count, color: "#34d399" },
              { icon: <CreditCard size={18} />, label: "Card", value: data.summary.card, count: data.summary.card_count, color: "#c9a84c" },
              { icon: <Smartphone size={18} />, label: "Instapay", value: data.summary.instapay, count: data.summary.instapay_count, color: "#a855f7" },
              { icon: <TrendingUp size={18} />, label: "Total", value: data.summary.total, count: data.payments.length, color: "#F2EBD9" },
            ].map(s => (
              <div key={s.label} style={{ background: "#0E1E38", border: "1px solid rgba(201,168,76,0.12)", borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ color: s.color, marginBottom: 6 }}>{s.icon}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>${s.value.toFixed(2)}</div>
                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", marginTop: 2 }}>{s.label} · {s.count} tx</div>
              </div>
            ))}
          </div>

          {/* Therapist commission */}
          {data.therapists.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(242,235,217,0.45)", marginBottom: 12 }}>Therapist Commission (10%)</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {data.therapists.map(t => (
                  <div key={t.name} style={{ background: "#0b1120", border: "1px solid rgba(201,168,76,0.1)", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 14 }}>
                    <User size={15} color="#818cf8" />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: "#F2EBD9", fontSize: 13 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)" }}>{t.sessions} session{t.sessions !== 1 ? "s" : ""} · Revenue: ${t.revenue.toFixed(2)}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: "#c9a84c" }}>${t.commission.toFixed(2)}</div>
                      <div style={{ fontSize: 10, color: "rgba(242,235,217,0.3)" }}>commission</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transaction list */}
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(242,235,217,0.45)", marginBottom: 12 }}>Transactions</div>
          {data.payments.length === 0 ? (
            <div style={{ color: "rgba(242,235,217,0.3)", fontSize: 13, textAlign: "center", padding: 20 }}>No payments recorded for this date</div>
          ) : (
            <div className="spa-appt-table">
              <div className="spa-table-header">
                {["Reference", "Note", "Method", "Amount", "Time"].map(h => <div key={h} className="spa-th">{h}</div>)}
              </div>
              {data.payments.map(p => (
                <div key={p.id} className="spa-table-row">
                  <div className="spa-td" style={{ fontFamily: "monospace", fontSize: 12 }}>{p.bookingid}</div>
                  <div className="spa-td">{p.note || "—"}</div>
                  <div className="spa-td">{methodLabel(p)}</div>
                  <div className="spa-td" style={{ fontWeight: 700, color: "#c9a84c" }}>${p.amount?.toFixed(2)}</div>
                  <div className="spa-td">{fmtDT(p.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
